import { DatabaseFieldType, type DatabaseValue } from '@databases/types/database';
import { isValidHyperlinkUrl } from '@databases/utils/database/field-types/hyperlink-normalization';
import { createElement, type ReactNode } from 'react';
import { defineFieldReadModule, type FieldReadModule } from '../field-read-module.ts';
import {
  EMPTY_PAGE_LINK_CONTENT,
  type PageLinkReadContent
} from './page-link-read-content.ts';
import { PageLinkValueView } from './page-link-value-view.tsx';

export type PageLinkReadRenderer = (content: PageLinkReadContent) => ReactNode;
export type PageLinkReadPolicy = {
  readonly baseUrl: string;
  /**
   * Read an already available URL; this callback must not load or subscribe.
   */
  readonly getContentUrl?: (contentId: string) => string | undefined;
};
const renderPageLink: PageLinkReadRenderer = (content) =>
  createElement(PageLinkValueView, { content });

/**

 * Captures URL policy; mounted Smart Cards retain application-owned metadata resolution.

 */
export function createPageLinkFieldReadModule({
  baseUrl,
  getContentUrl,
  render = renderPageLink
}: PageLinkReadPolicy & {
  readonly render?: PageLinkReadRenderer;
}): FieldReadModule<PageLinkReadContent> {
  return defineFieldReadModule({
    fieldType: DatabaseFieldType.PAGE_LINK,
    render,
    compile: (field) =>
      Object.freeze({
        fieldId: field.id,
        resolve(
          storedValue: DatabaseValue | undefined,
          defaultValue: DatabaseValue | undefined
        ): PageLinkReadContent {
          const value = storedValue ?? defaultValue;

          if (value?.type !== DatabaseFieldType.PAGE_LINK) {
            return EMPTY_PAGE_LINK_CONTENT;
          }

          const links = value.value.flatMap((item) => {
            if (!('contentId' in item) || typeof item.contentId !== 'string') {
              return [];
            }

            const url =
              getContentUrl?.(item.contentId) ??
              `${baseUrl}/spaces/~/pages/${item.contentId}`;

            return [
              Object.freeze({
                key: item._id,
                url,
                href: url,
                label: '',
                resolve: isValidHyperlinkUrl(url)
              })
            ];
          });

          return links.length
            ? Object.freeze({ kind: 'page-link', links: Object.freeze(links) })
            : EMPTY_PAGE_LINK_CONTENT;
        }
      })
  });
}
